import { MantineColor } from '@mantine/core';
import { BRAND_COLORS } from './colorPalette';

/**
 * Score completion threshold definitions.
 *
 * Each level defines:
 *  - `minScore`      — minimum normalised score (0–1) to qualify for this level
 *  - `mantineColor`  — Mantine named colour for use in <Badge>, <Progress>, etc.
 *  - `brandColor`    — raw hex from the brand palette (for SVG / inline style use)
 *  - `labelKey`      — i18n translation key for the human-readable label
 *
 * Levels are ordered highest → lowest.  `getScoreThreshold()` walks the array
 * and returns the first level whose `minScore` the value meets.
 */
export interface ScoreThreshold {
    key: string;
    minScore: number;
    mantineColor: MantineColor;
    brandColor: string;
    labelKey: string;
}

export const SCORE_THRESHOLDS = [
    {
        key: 'excellent',
        minScore: 0.7,
        mantineColor: 'sopraGreen' as MantineColor,
        brandColor: BRAND_COLORS.darkGreen,     // #00a188
        labelKey: 'PROFILE_COMPLETION_SCORE_EXCELLENT',
    },
    {
        key: 'good',
        minScore: 0.5,
        mantineColor: 'materialAmber' as MantineColor,
        brandColor: BRAND_COLORS.orange,        // #ef7d00
        labelKey: 'PROFILE_COMPLETION_SCORE_GOOD',
    },
    {
        key: 'needsWork',
        minScore: 0.3,
        mantineColor: 'sopraOrange' as MantineColor,
        brandColor: BRAND_COLORS.darkerOrange,  // #E67100
        labelKey: 'PROFILE_COMPLETION_SCORE_NEEDS_WORK',
    },
    {
        key: 'incomplete',
        minScore: 0,
        mantineColor: 'sopraPrimary' as MantineColor,
        brandColor: BRAND_COLORS.red,           // #cf022b
        labelKey: 'PROFILE_COMPLETION_SCORE_INCOMPLETE',
    },
] as const satisfies readonly ScoreThreshold[];

export type ScoreThresholdKey = typeof SCORE_THRESHOLDS[number]['key'];

/** Returns the threshold entry that matches the given 0–1 score. */
export const getScoreThreshold = (score: number): ScoreThreshold =>
    SCORE_THRESHOLDS.find((t) => score >= t.minScore) ?? SCORE_THRESHOLDS[SCORE_THRESHOLDS.length - 1];
